import React, { useState } from 'react';
import { Row, Col, Card, Button, Form, Input, Typography, message, Modal, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getUserData, saveUserData } from '../constants';
import axiosInstance from '../../axiosInstance';
import { LockOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const Login = () => {
    const [loading, setLoading] = useState(false);
    const [isForgotPasswordModalVisible, setIsForgotPasswordModalVisible] = useState(false);
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

    const navigate = useNavigate();

    const handleSubmit = (values) => {
        setLoading(true);

        axiosInstance.post('login', values)
            .then(response => {
                if (response.status === 200) {
                    const { user, token } = response.data;

                    localStorage.setItem('token', token);
                    axiosInstance.defaults.headers['Authorization'] = `Bearer ${token}`;
                    const shops = user.employees?.map(employee => employee.shop) || [];
                    saveUserData({
                        id: user.id,
                        retailer_id: user.retailer_id,
                        name: user.name,
                        email: user.email,
                        phone: user.phone,
                        profile_pic: user.profile_pic,
                        role: user.role,
                        shops: shops,  
                        user_permissions: user.user_permission
                    });
                    localStorage.setItem('retailerId', user.retailer_id);
                    const userData = getUserData() || {};
                    const userShops = userData?.shops || [];
                    const selectedShop = userShops.length > 0 ? userShops[0] : null;
                    localStorage.setItem('employeeId', user.id);

                    if (selectedShop) {
                        localStorage.setItem('shopId', selectedShop.id);
                        message.success(`Default shop set to: ${selectedShop.name}`);
                    }

                    return axiosInstance.get('check-package-limit');
                }
                throw new Error('Login failed');
            })
            .then((middlewareResponse) => {
                if (middlewareResponse.status === 200) {
                    navigate('/');
                    window.location.reload();
                }
            })
            .catch(error => {
                if (error.response) {
                    if (error.response.status === 401) {
                        message.error('Invalid credentials');
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                    } else if (error.response.status === 422) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        message.error('Validation error');
                    } else if (error.response.status === 403) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        message.error(error.response.data.message);
                        navigate('/in-touch/upgrade');
                    } else if (error.response.status === 404) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        message.error(error.response.data.message);
                        navigate('/in-touch/register');
                    } else {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        message.error('An error occurred. Please try again later.');
                    }
                } else {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    message.error('Network error. Please try again later.');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleForgotPassword = (values) => {
        setForgotPasswordLoading(true);

        axiosInstance.post('reset-password', values)
            .then(response => {
                if (response.status === 200) {
                    message.success(response.data.message);
                    setIsForgotPasswordModalVisible(false);
                }
            })
            .catch(error => {
                if (error.response && error.response.status === 404) {
                    message.error('Phone number not found.');
                } else if (error.response && error.response.status === 422) {
                    const errors = error.response.data.errors;
                    if (errors.phone) {
                        message.error(errors.phone[0]);
                    } else {
                        message.error('Validation error.');
                    }
                } else {
                    message.error('An error occurred. Please try again later.');
                }
            })
            .finally(() => {
                setForgotPasswordLoading(false);
            });
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: '500px',
                    borderRadius: '12px',
                    boxShadow: '0 20px 27px rgba(0, 0, 0, 0.05)',
                    border: 'none',
                    overflow: 'hidden'
                }}
                bodyStyle={{
                    padding: '40px'
                }}
            >
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <img
                        src="/images/sell-smart-logo.png"
                        alt="Sell Smart Logo"
                        style={{ height: '60px', marginBottom: '16px' }}
                    />
                    <Title level={3} style={{ marginBottom: '8px', color: '#2d3748' }}>
                        Welcome Back!
                    </Title>
                    {/* <Text type="secondary" style={{ fontSize: '16px' }}>
                        sign in
                    </Text> */}
                </div>

                <Form
                    layout="vertical"
                    onFinish={handleSubmit}
                    size="large"
                >
                    <Form.Item
                        name="email"
                        rules={[
                            { required: true, message: 'Email is required' },
                            { type: 'email', message: 'Must be in email format' },
                        ]}
                        style={{ marginBottom: '24px' }}
                    >
                        <Input
                            prefix={<MailOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />}
                            placeholder="Email"
                            style={{
                                height: '48px',
                                borderRadius: '12px',
                                padding: '12px 16px'
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[{ required: true, message: 'Password is required' }]}
                        style={{ marginBottom: '12px' }}
                    >
                        <Input.Password
                            prefix={<LockOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />}
                            placeholder="Password"
                            style={{
                                height: '48px',
                                borderRadius: '12px',
                                padding: '12px 16px'
                            }}
                        />
                    </Form.Item>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: '24px'
                    }}>
                        <Text
                            style={{
                                color: '#4299e1',
                                cursor: 'pointer',
                                fontWeight: '500'
                            }}
                            onClick={() => setIsForgotPasswordModalVisible(true)}
                        >
                            Forgot password?
                        </Text>
                    </div>

                    <Form.Item style={{ marginBottom: '16px' }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            style={{
                                height: '48px',
                                borderRadius: '12px',
                                fontWeight: '600',
                                fontSize: '16px',
                                background: '#4299e1',
                                borderColor: '#4299e1'
                            }}
                        >
                            Sign In
                        </Button>
                    </Form.Item>

                    <Divider style={{ color: '#a0aec0' }}>or</Divider>

                    <div style={{ textAlign: 'center', marginTop: '24px' }}>
                        <Text style={{ color: '#718096' }}>
                            Don't have an account?{' '}
                            <Text
                                strong
                                style={{ color: '#4299e1', cursor: 'pointer' }}
                                onClick={() => navigate('/in-touch/register')}
                            >
                                Sign up
                            </Text>
                        </Text>
                    </div>
                </Form>
            </Card>

            <Modal
                title={<Title level={4} style={{ marginBottom: 0 }}>Reset Password</Title>}
                visible={isForgotPasswordModalVisible}
                onCancel={() => setIsForgotPasswordModalVisible(false)}
                footer={null}
                centered
            >
                <Form layout="vertical" onFinish={handleForgotPassword}>
                    <Form.Item
                        name="phone"
                        rules={[{ required: true, message: 'Phone number is required' }]}
                        style={{ marginBottom: '24px' }}
                    >
                        <Input
                            prefix={<PhoneOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />}
                            placeholder="Registered phone number"
                            style={{
                                height: '48px',
                                borderRadius: '12px',
                                padding: '12px 16px'
                            }}
                        />
                    </Form.Item>

                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={forgotPasswordLoading}
                        block
                        style={{
                            height: '48px',
                            borderRadius: '12px',
                            fontWeight: '600',
                            fontSize: '16px',
                            background: '#4299e1',
                            borderColor: '#4299e1'
                        }}
                    >
                        Reset Password
                    </Button>
                </Form>
            </Modal>
        </div>
    );
};

export default Login;